import { useQuery } from '@apollo/client';
import { useMemo } from 'react';
import dayjs from 'dayjs';
import { DayjsFormat, getVendorIds, resolveGqlErrors } from '@/helpers';
import { useIdlePooling } from '@/hooks';
import { getConfirmedEvents } from '@/api/operations/get-confirmed-events-search';
import { aggregateDeliveryEventVendors, getEventPreview } from '../helpers';
import { DateEvents, EventPreview, SearchDeliveryEvent, SearchPopupEvent } from '../types';
import { EventProduct, EventStatus } from '@/api';

type UseConfirmedSection = () => {
  loading: boolean;
  error?: string;
  eventsByDate: DateEvents[];
};

const useConfirmedSection: UseConfirmedSection = () => {
  const vendorIds = getVendorIds();
  const startTimeAfter = useMemo(() => dayjs().subtract(1, 'day').format(), []);
  const startTimeBefore = useMemo(() => dayjs().add(2, 'day').endOf('day').format(), []);

  const {
    loading,
    data: eventsData,
    error,
    stopPolling,
    startPolling,
  } = useQuery(getConfirmedEvents, {
    variables: {
      vendorIds,
      startTimeAfter,
      startTimeBefore,
    },
    skip: vendorIds.length === 0,
  });
  useIdlePooling({ startPolling, stopPolling });

  const eventsByDate: DateEvents[] = [];

  const filteredEvents = eventsData?.searchEventVendors?.eventVendors?.nodes
    ?.filter((event) => event !== null)
    .filter((event) => {
      if (event.productType === EventProduct.POPUP || event.productType === EventProduct.CAFE) {
        return (
          (event as SearchPopupEvent).popupEvent.status.toUpperCase() !== EventStatus.SCHEDULED
        );
      }
      if (event.productType === EventProduct.DELIVERY) {
        return (event as SearchDeliveryEvent).selectEvent.status !== EventStatus.SCHEDULED;
      }
      return true;
    });

  const confirmedEvents = getEventPreview(aggregateDeliveryEventVendors(filteredEvents));

  const events = confirmedEvents.toSorted(
    (a, b) => dayjs(a.eventTime).valueOf() - dayjs(b.eventTime).valueOf()
  );

  events.forEach((event: EventPreview) => {
    const date = dayjs(event.eventTime).format(DayjsFormat.CALENDAR_DATE);

    const index = eventsByDate.findIndex((group) => group.date === date);

    if (index === -1) {
      eventsByDate.push({ date, events: [event] });
      return;
    }

    eventsByDate[index].events.push(event);
  });

  return {
    loading,
    error: resolveGqlErrors(error || eventsData?.searchEventVendors?.error),
    events,
    eventsByDate,
  };
};

export default useConfirmedSection;
